import { useMemo, useState } from "react";
import { useCoins } from "../core";
import { ASSETS, COINS } from "../core/utils/assetIcons";
import { toNumber } from "../core/utils/format";
import Pill from "./shared/pill";
import Header from "./shared/header";
import SkeletonRow from "./table/skeleton";
import PageDescription from "./shared/description";
import PulseCell from "./table/pulseCell";
import SearchInput from "./shared/searchInput";

export default function CoinList() {
  const {
    coins,
    loading,
    error,
    refresh,
    categories,
    selectedCategory,
    setSelectedCategory,
  } = useCoins();

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const filteredCoins = useMemo(() => {
    const selected = (selectedCategory || "").toString().trim().toLowerCase();

    const searchTerm = search.trim().toLowerCase();

    const normalizedCoins = coins.map((c) => ({
      ...c,
      category:
        c.category?.toLowerCase() === "uncategorized"
          ? "crypto"
          : (c.category || "").toLowerCase(),
    }));

    return normalizedCoins.filter((coin) => {
      const categoryMatch =
        selected === "all" || !selected ? true : coin.category === selected;

      const searchMatch =
        !searchTerm ||
        coin.ticker?.toLowerCase().includes(searchTerm) ||
        coin.name?.toLowerCase().includes(searchTerm);

      return categoryMatch && searchMatch;
    });
  }, [coins, selectedCategory, search]);

  const sorted = [...filteredCoins].sort((a, b) => {
    if (!sortField) return 0;

    let va = 0;
    let vb = 0;

    if (sortField === "changeValue") {
      va = toNumber(a.change?.value);
      vb = toNumber(b.change?.value);
    } else if (sortField === "changePercent") {
      va = toNumber(a.change?.percent);
      vb = toNumber(b.change?.percent);
    } else {
      va = toNumber(a[sortField]);
      vb = toNumber(b[sortField]);
    }

    return sortDir === "asc" ? va - vb : vb - va;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return <span style={{ opacity: 0.25, fontSize: 10 }}>▲</span>;
    return (
      <span style={{ fontSize: 10, color: "var(--primary)" }}>
        {sortDir === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  return (
    <div className="min-h-screen w-full text-white">
      <Header refresh={refresh} />

      <main className="bg-[var(--bg-lighter)]">
        <PageDescription />

        <div className="bg-[var(--bg)] rounded-2xl shadow-lg">
          <div className="overflow-x-auto scroll-fade hide-scrollbar border-b border-[var(--bg-lighter)]">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-4 px-4 md:px-0 py-4 w-max">
                {categories?.map((category) => {
                  return (
                    <Pill
                      key={category}
                      size="md"
                      className="uppercase"
                      onClick={() => setSelectedCategory(category)}
                      active={selectedCategory === category}
                    >
                      {category}
                    </Pill>
                  );
                })}
              </div>
            </div>
          </div>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search BTC, GOLD, NVDA..."
          />
          <div className="overflow-x-auto pb-8">
            <table className="w-full table-fixed max-w-6xl mx-auto min-w-[370px]">
              <thead className="">
                <tr className="font-sans font-semibold tracking-wide w-full text-[var(--primary)] text-xs">
                  <th className="text-left py-4 px-4 md:px-0">Asset</th>
                  <th
                    className="text-right cursor-pointer select-none px-4 md:px-0"
                    onClick={() => handleSort("markPx")}
                  >
                    <span className="flex items-center justify-end gap-1.5">
                      Last <SortIcon field="markPx" />
                    </span>
                  </th>
                  <th
                    className="text-right py-4 px-4 md:px-0"
                    onClick={() => handleSort("changeValue")}
                  >
                    <span className="flex items-center justify-end gap-1.5">
                      Change <SortIcon field="changeValue" />
                    </span>
                  </th>

                  <th
                    className="text-right py-4  cursor-pointer hidden sm:block"
                    onClick={() => handleSort("changePercent")}
                  >
                    <span className="flex items-center justify-end gap-1.5">
                      Change % <SortIcon field="changePercent" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? COINS.map((_, i) => <SkeletonRow key={i} index={i} />)
                  : sorted.map((coin, idx) => {
                      // const flash = flashMap[coin.id];
                      const ticker = (
                        coin.ticker ||
                        coin.symbol ||
                        ""
                      ).toUpperCase();
                      return (
                        <tr
                          key={coin.id}
                          className={`table-row row-enter`}
                          style={{
                            borderBottom:
                              idx < sorted.length - 1
                                ? "1px solid rgba(255,255,255,0.04)"
                                : "none",
                            animationDelay: `${idx * 0.04}s`,
                          }}
                        >
                          <td className="text-left font-sans px-4 md:px-0">
                            <div className="flex items-center gap-3">
                              {/* <AssetIcon ticker={ticker} /> */}
                              <div>
                                <p className="font-sans tracking-wide text-base font-medium line-height-1 text-[var(--foreground)]">
                                  {ticker}
                                </p>
                                <p className="hidden sm:block font-sans text-xs text-[var(--foreground-muted)]">
                                  {ASSETS?.[ticker]?.name || ticker}
                                </p>

                                <Pill
                                  size="xs"
                                  active
                                  variant="primary"
                                  className="sm:hidden uppercase"
                                >
                                  {coin.category}
                                </Pill>
                              </div>
                            </div>
                          </td>
                          <td className="text-right py-4 px-4 md:px-0">
                            <PulseCell
                              value={coin.markPx}
                              formatter={(v) =>
                                `$${Number(v).toLocaleString()}`
                              }
                              className="font-nums text-base font-semibold md:font-medium text-[var(--foreground)]"
                            />
                          </td>

                          {/* 24h Change */}
                          <td className="text-right py-4 px-4 md:px-0">
                            <PulseCell
                              value={toNumber(coin.change?.value)}
                              formatter={(v) =>
                                v.toString().replace(/[+-]/g, "")
                              }
                              className="font-nums md:text-sm text-base font-semibold line-height-1 text-[var(--foreground)]"
                            />
                            <p
                              className={`md:hidden font-nums text-sm font-semibold line-height-1 text-[var(--foreground)] mb-1 ${
                                toNumber(coin.change?.percent) >= 0
                                  ? "text-[var(--green)]"
                                  : "text-[var(--red)]"
                              }`}
                            >
                              {coin.change.percent}
                            </p>
                          </td>

                          <td className="text-right py-4 px-4 md:px-0 hidden sm:block">
                            <span
                              className={`font-nums text-sm font-semibold line-height-1 text-[var(--foreground)] mb-1 ${
                                toNumber(coin.change?.percent) >= 0
                                  ? "text-[var(--green)]"
                                  : "text-[var(--red)]"
                              }`}
                            >
                              {coin.change.percent}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
